// @flow

import * as React from 'react';

import { AddressField, PhoneField } from '@gandi/react-places';
import type { Organization } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';
import { Alert, Base } from '@gandi/ui';

import {
  Form,
  InputTextField,
  TogglePillField,
  ToggleGroupField,
  type FormComponentProps,
} from 'gandi.react-form';

import type { CustomerDataForm, CustomerDetail } from '../../types';

export type StateProps = {
  formValueType?: CustomerDataForm['type'],
};

type FormProps = {
  ...Partial<FormComponentProps>,
  change: (field: string, value: mixed) => void,
  handleSubmit: (data: CustomerDataForm) => Promise<any>,
  invalid?: boolean,
  form: string,
  submitFailed: boolean,
};

export type OwnProps = {
  // eslint-disable-next-line react/no-unused-prop-types -- to remove?
  onSubmit?: (data: CustomerDataForm) => Promise<any>,
  buttons: Array<React.MixedElement>,
  initialValues?: CustomerDetail | Organization,
  isCreation: boolean,
};

type InjectedProps = {
  ...FormProps,
  translator: TranslatorProps,
};

type TypeItem = {
  value: string,
  text: string,
};

export type Props = {
  ...InjectedProps,
  ...StateProps,
  ...OwnProps,
};

export default class CustomerForm extends React.Component<Props> {
  static FORM_ID: string = 'customer-form';

  typeItems: Array<{ text: string, value: string }> = [
    { value: 'individual', text: this.props.translator.__('Individual') },
    { value: 'company', text: this.props.translator.__('Company') },
    { value: 'association', text: this.props.translator.__('Association') },
    { value: 'publicbody', text: this.props.translator.__('Public body') },
  ];

  renderTypePill: (typeItem: TypeItem) => React.Node = (typeItem: TypeItem) => (
    <TogglePillField
      id={typeItem.value}
      key={typeItem.value}
      value={typeItem.value}
    >
      {typeItem.text}
    </TogglePillField>
  );

  render(): React.MixedElement {
    const { __ } = this.props.translator;
    const isNotNewIndividual =
      !!this.props.formValueType && this.props.formValueType !== 'individual';
    const isNotIndividual =
      this.props.initialValues &&
      this.props.initialValues.type !== 'individual';
    const hasType = !!this.props.formValueType;

    return (
      <div className="form-create form-centered">
        {this.props.submitFailed && this.props.invalid && (
          <Base marginBottom="base">
            <Alert
              message={__('Please correct the errors below to continue.')}
              title={__('Submission error')}
              intent="danger"
            />
          </Base>
        )}

        {!this.props.isCreation && (
          <Base marginBottom="base">
            <Alert
              message={__(
                'Changes made in this form will not be applied on the products ' +
                  'already owned by your customer. They will only be used for future purchases.',
              )}
              intent="warning"
            />
          </Base>
        )}

        <Form
          className="form-stacked wrapper-plain"
          id={this.props.form}
          method="post"
          noValidate
          onSubmit={this.props.handleSubmit}
        >
          <div className="form-notice form-notice-required">
            *{__('Required fields')}
          </div>

          {this.props.isCreation && (
            <ToggleGroupField
              id="type"
              type="radio"
              name="type"
              label={__('Type')}
              value={this.props.formValueType}
            >
              {this.typeItems.map(this.renderTypePill)}
            </ToggleGroupField>
          )}

          {(isNotNewIndividual || isNotIndividual) && (
            <InputTextField
              defaultValue={
                this.props.initialValues && this.props.initialValues.orgname
              }
              disabled={!this.props.isCreation}
              help={__(
                'This is the full real name of the company, association, or public body of your customer.' +
                  'It will be used on invoices and in every legal aspect of your experience at Gandi.',
              )}
              key="orgname"
              label={__('Company name')}
              name="orgname"
              required
            />
          )}
          {hasType && [
            <InputTextField
              disabled={
                !this.props.isCreation &&
                this.props.initialValues &&
                this.props.initialValues.type === 'individual'
              }
              key="fname"
              label={__('First name')}
              name="firstname"
              required
            />,
            <InputTextField
              disabled={
                !this.props.isCreation &&
                this.props.initialValues &&
                this.props.initialValues.type === 'individual'
              }
              key="lname"
              label={__('Last name')}
              name="lastname"
              required
            />,
            <AddressField
              forceMandatorySubdivision
              change={this.props.change}
              form={this.props.form}
              key="address"
            />,
            <InputTextField
              key="email"
              label={__('Email')}
              name="email"
              required
              type="email"
            />,
            <PhoneField
              key="phone"
              label={__('Phone')}
              name="phone"
              required
            />,
            <PhoneField key="fax" label={__('Fax')} name="fax" />,
          ]}
        </Form>
        {hasType && (
          <Base flex flexJustifyBetween flexWrap marginY="base">
            {this.props.buttons}
          </Base>
        )}
      </div>
    );
  }
}
